/**
 * The Strip Card Game tab's reads and writes (ISSUE-1288, ISSUE-1290 to
 * ISSUE-1292, ISSUE-1296).
 *
 * Reads: `/strips/pool-get` for the game switcher and `/strips/card-get` for
 * the board, polled at the server's `refreshAfterSeconds` while the browser
 * tab is visible (react-query pauses an interval in a hidden tab) and
 * refetched on focus by the app's query defaults. v2 has no websocket, so
 * this is how another member's claim reaches the board.
 *
 * Writes: one generated mutation per intent. A claim and a release paint the
 * row before the server answers and roll back on a refusal; every refusal
 * raises the shared error modal and refetches the card, so a lost race
 * (`STRIP_SPOT_TAKEN`) shows the row's real holder.
 */
import {
  useStripsCardGet,
  useStripsCustomGameAdd,
  useStripsCustomGameRename,
  useStripsGameAdd,
  useStripsGameRemove,
  useStripsPoolGet,
  useStripsSpotAssign,
  useStripsSpotClaim,
  useStripsSpotRelease,
  useStripsStripAdd,
  useStripsStripCopyUsers,
  useStripsStripLock,
  useStripsStripUnlock,
  useStripsWinnersSet,
} from "@sportsmet/contest-sdk/private";
import { useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  applyOptimisticSpot,
  newStripTransactionId,
  resolveSelectedEventId,
  resolveSelectedStripNumber,
  stripCardQueryKey,
  stripRefreshIntervalMs,
  type typeStripCardEnvelope,
} from "./stripBoardModel";

const CARD_QUERY_PREFIX = ["post:strips:stripsCardGet", "svc:contest-api"];
const POOL_QUERY_PREFIX = ["post:strips:stripsPoolGet", "svc:contest-api"];
/**
 * The pool header's and the pools list's status line ("Card open · You have
 * 3 spots") are pool-api reads. A write that moves who holds a row changes
 * that count, and no generated dependency can reach across services, so
 * those writes refresh the two reads themselves.
 */
export const POOL_STATUS_QUERY_PREFIXES = [
  ["post:pool:poolGet", "svc:pool-api"],
  ["post:pool:poolListMine", "svc:pool-api"],
] as const;

export type typeStripWinnerDraftInput = {
  stripWinnerId: string | null;
  wonAt: string;
  note: string;
};

/** Which control is busy, so only the tapped one shows a spinner. */
export type typeStripBusy =
  | { kind: "row"; stripNumber: number; row: number }
  | { kind: "addStrip" }
  | { kind: "lock"; stripNumber: number }
  | { kind: "unlock"; stripNumber: number }
  | { kind: "copy"; stripNumber: number }
  | { kind: "assign" }
  | { kind: "winners" }
  | { kind: "addGame" }
  | { kind: "renameGame" }
  | { kind: "removeGame" }
  | null;

export function useStripCardGame(poolId: string) {
  const queryClient = useQueryClient();
  // A Strip Card alert links to its game through the pools route's eventId.
  const search = useSearch({ strict: false });
  const [pickedEventId, setPickedEventId] = useState<string | null>(null);
  const [pickedStripNumber, setPickedStripNumber] = useState<number | null>(
    null,
  );
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<typeStripBusy>(null);
  // One id per Add a strip intent. It survives a failed tap so the retry is
  // recognized, and is replaced once the server answers.
  const [addStripTransactionId, setAddStripTransactionId] = useState<string>(
    () => newStripTransactionId(),
  );

  const poolRead = useStripsPoolGet({ poolId });
  const poolData = poolRead.data?.data ?? null;
  const games = poolData?.games ?? [];
  const eventId = resolveSelectedEventId({
    games,
    serverSelectedEventId: poolData?.selectedEventId,
    picked: pickedEventId,
    linked: typeof search.eventId === "string" ? search.eventId : null,
  });

  const cardRead = useStripsCardGet(
    { poolId, eventId: eventId ?? "" },
    {
      enabled: eventId !== null,
      refetchInterval: (query) =>
        stripRefreshIntervalMs(query.state.data?.data?.refreshAfterSeconds),
    },
  );
  const card = cardRead.data?.data ?? null;
  const selectedStripNumber = resolveSelectedStripNumber({
    strips: card?.strips ?? [],
    picked: pickedStripNumber,
  });

  const claimMutation = useStripsSpotClaim();
  const releaseMutation = useStripsSpotRelease();
  const assignMutation = useStripsSpotAssign();
  const addStripMutation = useStripsStripAdd();
  const lockMutation = useStripsStripLock();
  const unlockMutation = useStripsStripUnlock();
  const copyMutation = useStripsStripCopyUsers();
  const winnersMutation = useStripsWinnersSet();
  const gameAddMutation = useStripsGameAdd();
  const gameRemoveMutation = useStripsGameRemove();
  const customGameAddMutation = useStripsCustomGameAdd();
  const customGameRenameMutation = useStripsCustomGameRename();
  // One id per Add a custom game intent, like Add a strip: a retried tap
  // after a lost answer adds one game, not two.
  const [customGameTransactionId, setCustomGameTransactionId] =
    useState<string>(() => newStripTransactionId());

  function refreshPoolStatus(): void {
    for (const queryKey of POOL_STATUS_QUERY_PREFIXES) {
      void queryClient.invalidateQueries({ queryKey: [...queryKey] });
    }
  }

  function refetchBoard(): void {
    void queryClient.invalidateQueries({ queryKey: CARD_QUERY_PREFIX });
    void queryClient.invalidateQueries({ queryKey: POOL_QUERY_PREFIX });
  }

  /**
   * Runs one write. A refusal raises the error modal and refetches the
   * board; a request that never reached the server is already reported by
   * the app's MutationCache, so it only refetches. Resolves to the answer's
   * data, or null when there was none.
   */
  async function runWrite<T extends { error?: unknown; data?: unknown }>(
    nextBusy: typeStripBusy,
    write: () => Promise<T>,
    onRefusal?: () => void,
    movesSpots = false,
  ): Promise<NonNullable<T["data"]> | null> {
    setBusy(nextBusy);
    try {
      let result: T;
      try {
        result = await write();
      } catch {
        onRefusal?.();
        refetchBoard();
        return null;
      }
      if (showApiError(result)) {
        onRefusal?.();
        refetchBoard();
        return null;
      }
      if (movesSpots) refreshPoolStatus();
      return result.data ?? null;
    } finally {
      setBusy(null);
    }
  }

  async function changeSpot(
    kind: "claim" | "release",
    stripNumber: number,
    row: number,
  ): Promise<void> {
    if (eventId === null || busy !== null) return;
    setNote(null);
    const key = stripCardQueryKey({ poolId, eventId });
    await queryClient.cancelQueries({ queryKey: key });
    const snapshot = queryClient.getQueryData<typeStripCardEnvelope>(key);
    if (snapshot?.data) {
      queryClient.setQueryData<typeStripCardEnvelope>(key, {
        ...snapshot,
        data: applyOptimisticSpot(snapshot.data, { stripNumber, row, kind }),
      });
    }
    const body = { poolId, eventId, stripNumber, row };
    await runWrite(
      { kind: "row", stripNumber, row },
      () =>
        kind === "claim"
          ? claimMutation.mutateAsync(body)
          : releaseMutation.mutateAsync(body),
      () => {
        if (snapshot !== undefined) queryClient.setQueryData(key, snapshot);
      },
      true,
    );
  }

  async function assign(input: {
    stripNumber: number;
    row: number;
    userId: string | null;
  }): Promise<boolean> {
    if (eventId === null) return false;
    const data = await runWrite(
      { kind: "assign" },
      () => assignMutation.mutateAsync({ poolId, eventId, ...input }),
      undefined,
      true,
    );
    return data !== null;
  }

  async function addStrip(): Promise<void> {
    if (eventId === null || busy !== null) return;
    const data = await runWrite({ kind: "addStrip" }, () =>
      addStripMutation.mutateAsync({
        poolId,
        eventId,
        transactionId: addStripTransactionId,
      }),
    );
    if (data !== null) {
      setAddStripTransactionId(newStripTransactionId());
      setPickedStripNumber(data.stripNumber);
    }
  }

  async function lock(stripNumber: number): Promise<boolean> {
    if (eventId === null) return false;
    const data = await runWrite({ kind: "lock", stripNumber }, () =>
      lockMutation.mutateAsync({ poolId, eventId, stripNumber }),
    );
    return data !== null;
  }

  async function unlock(stripNumber: number): Promise<boolean> {
    if (eventId === null) return false;
    const data = await runWrite({ kind: "unlock", stripNumber }, () =>
      unlockMutation.mutateAsync({ poolId, eventId, stripNumber }),
    );
    return data !== null;
  }

  async function copyUsers(input: {
    stripNumber: number;
    fromStripNumber: number;
  }): Promise<boolean> {
    if (eventId === null) return false;
    const data = await runWrite(
      { kind: "copy", stripNumber: input.stripNumber },
      () => copyMutation.mutateAsync({ poolId, eventId, ...input }),
      undefined,
      true,
    );
    return data !== null;
  }

  async function setWinners(input: {
    stripNumber: number;
    row: number;
    winners: typeStripWinnerDraftInput[];
  }): Promise<boolean> {
    // A second Save while one is in flight would insert the new winners twice.
    if (eventId === null || busy !== null) return false;
    const data = await runWrite({ kind: "winners" }, () =>
      winnersMutation.mutateAsync({ poolId, eventId, ...input }),
    );
    return data !== null;
  }

  async function addGame(nextEventId: string): Promise<boolean> {
    const data = await runWrite({ kind: "addGame" }, () =>
      gameAddMutation.mutateAsync({ poolId, eventId: nextEventId }),
    );
    if (data === null) return false;
    setPickedEventId(data.eventId);
    setPickedStripNumber(null);
    return true;
  }

  /** ISSUE-1299. A blank name lets the server pick "Custom game N". */
  async function addCustomGame(title: string): Promise<boolean> {
    const trimmed = title.trim();
    const data = await runWrite({ kind: "addGame" }, () =>
      customGameAddMutation.mutateAsync({
        poolId,
        title: trimmed === "" ? null : trimmed,
        transactionId: customGameTransactionId,
      }),
    );
    if (data === null) return false;
    setCustomGameTransactionId(newStripTransactionId());
    setPickedEventId(data.eventId);
    setPickedStripNumber(null);
    return true;
  }

  async function renameCustomGame(title: string): Promise<boolean> {
    if (eventId === null) return false;
    const data = await runWrite({ kind: "renameGame" }, () =>
      customGameRenameMutation.mutateAsync({
        poolId,
        eventId,
        title: title.trim(),
      }),
    );
    return data !== null;
  }

  async function removeGame(): Promise<boolean> {
    if (eventId === null) return false;
    const data = await runWrite({ kind: "removeGame" }, () =>
      gameRemoveMutation.mutateAsync({ poolId, eventId }),
    );
    if (data === null) return false;
    setPickedEventId(null);
    setPickedStripNumber(null);
    return true;
  }

  return {
    poolRead,
    poolData,
    games,
    eventId,
    cardRead,
    card,
    selectedStripNumber,
    note,
    busy,
    selectGame: (next: string) => {
      setNote(null);
      setPickedEventId(next);
      setPickedStripNumber(null);
    },
    selectStrip: (stripNumber: number) => {
      setPickedStripNumber(stripNumber);
    },
    showNote: (next: string) => {
      setNote(next);
    },
    dismissNote: () => {
      setNote(null);
    },
    claim: (stripNumber: number, row: number) => {
      void changeSpot("claim", stripNumber, row);
    },
    release: (stripNumber: number, row: number) => {
      void changeSpot("release", stripNumber, row);
    },
    assign,
    addStrip: () => {
      void addStrip();
    },
    lock,
    unlock,
    copyUsers,
    setWinners,
    addGame,
    addCustomGame,
    renameCustomGame,
    removeGame,
    refetch: refetchBoard,
  };
}

export type typeStripCardGame = ReturnType<typeof useStripCardGame>;
